import {
  type AnimationPlaybackControls,
  animate,
  useMotionValue,
} from "framer-motion";
import { type CSSProperties, useEffect, useId, useRef } from "react";

// Type definitions
type ResponsiveImage = {
  src: string;
  alt?: string;
  srcSet?: string;
};

type AnimationConfig = {
  preview?: boolean;
  scale: number;
  speed: number;
};

type NoiseConfig = {
  opacity: number;
  scale: number;
};

type ShadowOverlayProps = {
  type?: "preset" | "custom";
  presetIndex?: number;
  customImage?: ResponsiveImage;
  sizing?: "fill" | "stretch";
  color?: string;
  animation?: AnimationConfig;
  noise?: NoiseConfig;
  style?: CSSProperties;
  className?: string;
};

function mapRange(options: {
  value: number;
  from: [number, number];
  to: [number, number];
}): number {
  const { value, from, to } = options;
  const [fromLow, fromHigh] = from;
  const [toLow, toHigh] = to;

  if (fromLow === fromHigh) {
    return toLow;
  }
  const percentage = (value - fromLow) / (fromHigh - fromLow);
  return toLow + percentage * (toHigh - toLow);
}

const useInstanceId = (): string => {
  const id = useId();
  const cleanId = id.replace(/:/g, "");
  const instanceId = `shadowoverlay-${cleanId}`;
  return instanceId;
};

export function EtherealShadow({
  sizing = "fill",
  color = "rgba(128, 128, 128, 1)",
  animation,
  noise,
  style,
  className,
}: ShadowOverlayProps) {
  const id = useInstanceId();
  const animationEnabled = animation && animation.scale > 0;
  const feColorMatrixRef = useRef<SVGFEColorMatrixElement>(null);
  const hueRotateMotionValue = useMotionValue(180);
  const hueRotateAnimation = useRef<AnimationPlaybackControls | null>(null);

  const displacementScale = animation
    ? mapRange({ value: animation.scale, from: [1, 100], to: [20, 100] })
    : 0;
  const animationDuration = animation
    ? mapRange({ value: animation.speed, from: [1, 100], to: [1000, 50] })
    : 1;

  useEffect(() => {
    if (feColorMatrixRef.current && animationEnabled) {
      if (hueRotateAnimation.current) {
        hueRotateAnimation.current.stop();
      }
      hueRotateMotionValue.set(0);
      hueRotateAnimation.current = animate(hueRotateMotionValue, 360, {
        duration: animationDuration / 25,
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop",
        repeatDelay: 0,
        ease: "linear",
        delay: 0,
        onUpdate: (value: number) => {
          if (feColorMatrixRef.current) {
            feColorMatrixRef.current.setAttribute("values", String(value));
          }
        },
      });

      return () => {
        if (hueRotateAnimation.current) {
          hueRotateAnimation.current.stop();
        }
      };
    }
  }, [animationEnabled, animationDuration, hueRotateMotionValue]);

  return (
    <div
      className={className}
      style={{
        overflow: "hidden",
        position: "absolute",
        width: "100%",
        height: "100%",
        inset: 0,
        ...style,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: -displacementScale,
          filter: animationEnabled ? `url(#${id}) blur(4px)` : "none",
        }}
      >
        {animationEnabled && (
          <svg
            aria-label="ethereal-shadow"
            role="img"
            style={{ position: "absolute" }}
          >
            <defs>
              <filter id={id}>
                <feTurbulence
                  baseFrequency={`${mapRange({ value: animation.scale, from: [0, 100], to: [0.001, 0.0005] })},${mapRange({ value: animation.scale, from: [0, 100], to: [0.004, 0.002] })}`}
                  numOctaves="2"
                  result="undulation"
                  seed="0"
                  type="turbulence"
                />
                <feColorMatrix
                  in="undulation"
                  ref={feColorMatrixRef}
                  type="hueRotate"
                  values="180"
                />
                <feColorMatrix
                  in="dist"
                  result="circulation"
                  type="matrix"
                  values="4 0 0 0 1  4 0 0 0 1  4 0 0 0 1  1 0 0 0 0"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="circulation"
                  result="dist"
                  scale={displacementScale}
                />
                <feDisplacementMap
                  in="dist"
                  in2="undulation"
                  result="output"
                  scale={displacementScale}
                />
              </filter>
            </defs>
          </svg>
        )}
        <div
          style={{
            backgroundColor: color,
            maskImage: `url('https://framerusercontent.com/images/ceBGguIpUU8luwByxuQz79t7To.png')`,
            maskSize: sizing === "stretch" ? "100% 100%" : "cover",
            maskRepeat: "no-repeat",
            maskPosition: "center",
            width: "100%",
            height: "100%",
          }}
        />
      </div>

      {noise && noise.opacity > 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("https://framerusercontent.com/images/g0QcWrxr87K0ufOxIUFBakwYA8.png")`,
            backgroundSize: noise.scale * 200,
            backgroundRepeat: "repeat",
            opacity: noise.opacity / 2,
          }}
        />
      )}
    </div>
  );
}
